import React, { useEffect, useState } from "react";
import { BASE_URL } from "../config/api"; // Ensure BASE_URL is correctly defined
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf"; // Import jsPDF
import "jspdf-autotable"; // Import for table support

const WalletList = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [balance, setBalance] = useState(null);
  const [startDate, setStartDate] = useState(""); // Add state for start date
  const [endDate, setEndDate] = useState(""); // Add state for end date
  const [transactionType, setTransactionType] = useState(""); // Add state for transaction type filter
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch transactions
    fetch(`${BASE_URL}wallet/transactions`)
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 1) {
          setTransactions(data.transactions);
          setFilteredTransactions(data.transactions);
        } else {
          alert("Failed to fetch wallet transactions.");
        }
      })
      .catch((error) =>
        console.error("Error fetching wallet transactions:", error)
      );

    // Fetch balance - try multiple endpoints
    const fetchBalance = async () => {
      const endpoints = [
        `${BASE_URL}wallet/balance?staff_id=1`,
        `${BASE_URL}wallet/balance`,
        `${BASE_URL}wallet`,
      ];

      for (let endpoint of endpoints) {
        try {
          const response = await fetch(endpoint);
          const data = await response.json();

          if (data.status === 1) {
            // Try different possible balance field names
            const balanceValue =
              data.balance ||
              data.data?.balance ||
              data.data ||
              data.total_balance;
            if (balanceValue !== undefined) {
              setBalance(balanceValue);
              return;
            }
          }
        } catch (error) {
          console.log(`Balance endpoint ${endpoint} failed:`, error);
        }
      }
      // If all endpoints fail, calculate from transactions
      calculateBalanceFromTransactions();
    };

    fetchBalance();
  }, []);

  // Calculate balance from transactions if API fails
  const calculateBalanceFromTransactions = () => {
    if (transactions.length === 0) return 0;

    const calculatedBalance = transactions.reduce((total, transaction) => {
      const amount = parseFloat(transaction.amount) || 0;
      if (transaction.transaction_type === "incoming") {
        return total + amount;
      } else if (transaction.transaction_type === "outgoing") {
        return total - amount;
      }
      return total;
    }, 0);

    setBalance(calculatedBalance);
  };

  // Handle search change
  const handleSearchChange = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
    filterTransactions(term, startDate, endDate, transactionType);
  };

  // Handle date change (start or end date)
  const handleDateChange = () => {
    filterTransactions(searchTerm, startDate, endDate, transactionType);
  };

  // Handle transaction type change
  const handleTransactionTypeChange = (event) => {
    const selectedType = event.target.value;
    setTransactionType(selectedType);
    filterTransactions(searchTerm, startDate, endDate, selectedType);
  };

  // Filter transactions based on search term, date range, and transaction type
  const filterTransactions = (term, start, end, type) => {
    let filtered = transactions.filter((transaction) => {
      // Check if transaction matches search term (name or transaction type)
      const matchesSearch =
        transaction.staff_name.toLowerCase().includes(term.toLowerCase()) ||
        transaction.transaction_type.toLowerCase().includes(term.toLowerCase());

      // Date filtering
      const transactionDate = new Date(transaction.created_at)
        .toISOString()
        .split("T")[0]; // Get date in YYYY-MM-DD format
      const matchesDateRange =
        (!start || transactionDate >= start) &&
        (!end || transactionDate <= end);

      // Filter by transaction type
      const matchesType =
        !type ||
        transaction.transaction_type.toLowerCase() === type.toLowerCase();

      return matchesSearch && matchesDateRange && matchesType;
    });

    setFilteredTransactions(filtered);
  };

  const handleAdd = () => navigate("/AddWallet");
  const handleAdds = () => navigate("/AddsWallet");
  const handleEdit = (id) => navigate(`/EditWallet/${id}`);
  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this transaction?")) {
      fetch(`${BASE_URL}wallet/transaction/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.status === 1) {
            alert(`Transaction ${id} deleted successfully!`);
            setTransactions(
              transactions.filter((transaction) => transaction.id !== id)
            );
            setFilteredTransactions(
              filteredTransactions.filter(
                (transaction) => transaction.id !== id
              )
            );
          } else {
            alert("Failed to delete transaction.");
          }
        })
        .catch((error) => {
          console.error("Error deleting transaction:", error);
          alert("There was an error deleting the transaction.");
        });
    }
  };

  // Format the date in DD/MM/YYYY format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = ("0" + date.getDate()).slice(-2);
    const month = ("0" + (date.getMonth() + 1)).slice(-2);
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Export the filtered transactions to PDF
  const handleExportPDF = () => {
    const doc = new jsPDF();

    // Add Title
    doc.text("Wallet Transactions", 14, 10);

    // Prepare table data for PDF
    const tableData = filteredTransactions.map((transaction) => [
      transaction.staff_name || "N/A",
      transaction.amount || "N/A",
      transaction.transaction_type || "N/A",
      transaction.description || "N/A",
      new Date(transaction.created_at).toLocaleString(),
    ]);

    // Add table to PDF with autoTable
    doc.autoTable({
      head: [
        ["Staff Name", "Amount", "Transaction Type", "Description", "Date"],
      ],
      body: tableData,
    });

    // Save the PDF with a filename
    doc.save("wallet_transactions.pdf");
  };

  return (
    <div id="main">
      <header className="mb-3">
        <a href="#" className="burger-btn d-block d-xl-none">
          <i className="bi bi-justify fs-3"></i>
        </a>
      </header>

      <div className="page-heading">
        <div className="page-title">
          <div className="row">
            <div className="col-12 col-md-6 order-md-1 order-last">
              <h3>Cash Wallet</h3>
            </div>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-12 col-md-12">
            <div className="card">
              <div className="card-header">
                <div className="row">
                  <div className="col-md-4">
                    <h5>
                      Total Balance:{" "}
                      {balance !== null ? `Rs. ${balance}` : "Loading..."}
                    </h5>
                  </div>
                  {/* <div className="col-md-2 text-end"><button className="btn btn-primary" onClick={handleAdd}>Payment</button><br></br><br></br></div> */}
                  {/* <div className="col-md-2 text-end"><button className="btn btn-primary" onClick={handleAdds}>Received</button><br></br><br></br></div> */}
                  <div className="col-md-8 text-end">
                    <button
                      className="btn btn-secondary ms-2"
                      onClick={handleExportPDF}
                    >
                      Export to PDF
                    </button>
                  </div>
                </div>
              </div>
              <div className="card-body">
                {/* Search and Date Filter Section */}
                <div className="row mb-3">
                  <div className="col-md-3">
                    <label htmlFor="searchTerm" className="form-label">
                      Search by Name
                    </label>
                    <input
                      type="text"
                      id="searchTerm"
                      className="form-control"
                      placeholder="Search by staff name"
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="startDate" className="form-label">
                      Start Date
                    </label>
                    <input
                      type="date"
                      id="startDate"
                      className="form-control"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="endDate" className="form-label">
                      End Date
                    </label>
                    <input
                      type="date"
                      id="endDate"
                      className="form-control"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="transactionType" className="form-label">
                      Transaction Type
                    </label>
                    <select
                      id="transactionType"
                      className="form-control"
                      value={transactionType}
                      onChange={handleTransactionTypeChange}
                    >
                      <option value="">All</option>
                      <option value="outgoing">Outgoing</option>
                      <option value="incoming">Incoming</option>
                    </select>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-4 mt-4">
                      <button
                        className="btn btn-secondary"
                        onClick={handleDateChange}
                      >
                        Apply Filters
                      </button>
                    </div>
                  </div>
                </div>

                {/* Transaction Type Filter Section */}

                <div className="table-responsive">
                  <table className="table table-lg">
                    <thead>
                      <tr>
                        <th>STAFF NAME</th>
                        <th>AMOUNT</th>
                        <th>TRANSACTION TYPE</th>
                        <th>DESCRIPTION</th>
                        <th>DATE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTransactions.length > 0 ? (
                        filteredTransactions.map((transaction) => (
                          <tr key={transaction.id}>
                            <td>{transaction.staff_name || "N/A"}</td>
                            <td>{transaction.amount || "N/A"}</td>
                            <td>{transaction.transaction_type || "N/A"}</td>
                            <td>{transaction.description || "N/A"}</td>
                            <td>{formatDate(transaction.created_at)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center">
                            No transactions found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WalletList;
